/**
 * The Support Inbox's left column: the community-room row, then the support
 * tickets for the active filter. Presentational — SupportInbox owns the data,
 * the filter and the selection; this renders rows and reports clicks.
 *
 * Each ticket row: initial avatar, name, how long since its last message,
 * the last-message preview, the status tag (from ticketState), an "N new"
 * pill, and — for tickets the user wrote last and nobody has answered in
 * 24h+ (`is_stale`) — an amber edge and "No reply 24h+" tag, so a forgotten
 * ticket stands out in any filter.
 */
import type { ChatInboxRoom } from "@/api/types";
import { fmtAgo, initials } from "@/utils/format";
import { ticketTag, type TicketTagKind } from "./ticketState";
import styles from "./TicketList.module.css";

// Avatar tints, picked per user so a row keeps its colour across refreshes.
const AVATAR_TINTS = ["--accent", "--violet", "--green", "--amber", "--red", "--gold"];

export function tintFor(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) | 0;
  return `var(${AVATAR_TINTS[Math.abs(h) % AVATAR_TINTS.length]})`;
}

const TAG_CLASS: Record<TicketTagKind, string> = {
  unclaimed: styles.tagUnclaimed,
  mine: styles.tagMine,
  claimed: styles.tagClaimed,
  closed: styles.tagClosed,
};

function HashIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <path d="M4 9h16M4 15h16M10 3 8 21M16 3l-2 18" />
    </svg>
  );
}

export function PublicRoomRow({
  joined,
  preview,
  selected,
  onSelect,
}: {
  joined: boolean;
  /** Latest cached community message, if any. */
  preview: string | null;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      className={`${styles.publicRow} ${selected ? styles.selected : ""}`}
      onClick={onSelect}
      aria-pressed={selected}
    >
      <span className={styles.publicIcon}>
        <HashIcon />
      </span>
      <span className={styles.body}>
        <span className={styles.name}>Public room</span>
        <span className={styles.preview}>{preview ?? "Community chat · all users + supporters"}</span>
      </span>
      <span className={`${styles.tag} ${joined ? styles.tagJoined : styles.tagClosed}`}>
        {joined ? "Joined" : "Not joined"}
      </span>
    </button>
  );
}

function TicketRow({
  room,
  viewerSub,
  selected,
  onSelect,
}: {
  room: ChatInboxRoom;
  viewerSub: string;
  selected: boolean;
  onSelect: () => void;
}) {
  const name = room.user_name || "Unknown user";
  const tag = ticketTag(room, viewerSub);
  return (
    <li>
      <button
        type="button"
        className={`${styles.row} ${selected ? styles.selected : ""} ${room.is_stale ? styles.stale : ""}`}
        onClick={onSelect}
        aria-pressed={selected}
        data-stale={room.is_stale}
      >
        <span className={styles.avatar} style={{ background: tintFor(room.user_id ?? room.id) }} aria-hidden="true">
          {initials(name, "")}
        </span>
        <span className={styles.body}>
          <span className={styles.topLine}>
            <span className={styles.name}>{name}</span>
            <span className={styles.time}>{fmtAgo(room.last_message_at)}</span>
          </span>
          <span className={styles.preview}>{room.preview ?? "No messages yet"}</span>
          <span className={styles.tags}>
            <span className={`${styles.tag} ${TAG_CLASS[tag.kind]}`} data-kind={tag.kind}>
              {tag.label}
            </span>
            {room.is_stale ? (
              <span className={`${styles.tag} ${styles.tagStale}`} title="The user wrote last and nobody has replied for over 24 hours">
                No reply 24h+
              </span>
            ) : null}
            {room.unread_count > 0 ? <span className={styles.unread}>{room.unread_count} new</span> : null}
          </span>
        </span>
      </button>
    </li>
  );
}

export function TicketList({
  rooms,
  viewerSub,
  selectedId,
  onSelect,
  status,
  error,
  hasMore,
  loadingMore,
  onLoadMore,
  onRetry,
}: {
  rooms: ChatInboxRoom[];
  viewerSub: string;
  selectedId: string | null;
  onSelect: (roomId: string) => void;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  onRetry: () => void;
}) {
  if (status === "failed" && rooms.length === 0) {
    return (
      <div className={styles.note} role="alert">
        {error ?? "Couldn't load tickets."}{" "}
        <button type="button" className={styles.linkButton} onClick={onRetry}>
          Retry
        </button>
      </div>
    );
  }
  if (rooms.length === 0) {
    return (
      <div className={styles.note}>
        {status === "succeeded" ? "All caught up." : "Loading tickets…"}
      </div>
    );
  }
  return (
    <>
      <ul className={styles.list} aria-label="Support tickets">
        {rooms.map((room) => (
          <TicketRow
            key={room.id}
            room={room}
            viewerSub={viewerSub}
            selected={room.id === selectedId}
            onSelect={() => onSelect(room.id)}
          />
        ))}
      </ul>
      {hasMore ? (
        <button type="button" className={styles.loadMore} onClick={onLoadMore} disabled={loadingMore}>
          {loadingMore ? "Loading…" : "Load more"}
        </button>
      ) : null}
    </>
  );
}
